import React, { useEffect, useState } from "react";
import {
  getAccessCodeFromUrl,
  getAccessErrorMessage,
  getStoredAccessSession,
  validateAccessCode,
} from "./accessService";

const copy = {
  tr: {
    eyebrow: "Someren Labs",
    title: "TA (3 EGO) Erişim Kontrolü",
    description: "Bu envanteri kullanmak için şirket, takım veya katılımcı erişim kodunuzu girin.",
    label: "Erişim kodu",
    placeholder: "Örnek: DEMO-TA-2026",
    button: "Envantere Giriş",
    checking: "Kontrol ediliyor...",
    durationNote: "⏱ Yetişkinler bu envanteri genelde 6-7 dakikada tamamlar.",
    warningNote: "Dikkat: En gerçekçi sonuç için ara vermeden tek oturumda tamamlamanızı ve aklınıza ilk gelen cevaplarla ilerlemenizi öneriyoruz.",
  },
  en: {
    eyebrow: "Someren Labs",
    title: "TA (3 EGO) Access Control",
    description: "Please enter your company, team or participant access code to use this inventory.",
    label: "Access code",
    placeholder: "Example: DEMO-TA-2026",
    button: "Enter Inventory",
    checking: "Checking...",
    durationNote: "⏱ Adults usually complete this inventory in about 6–7 minutes.",
    warningNote: "Please note: For the most accurate results, we recommend completing the inventory in a single uninterrupted session and going with your first, instinctive responses.",
  },
};

export default function AccessGuard({ language = "tr", onAccessReady, children }) {
  const [access, setAccess] = useState(null);
  const [code, setCode] = useState("");
  const [checking, setChecking] = useState(true);
  const [error, setError] = useState("");
  const text = copy[language] || copy.tr;

  useEffect(() => {
    let alive = true;

    async function bootstrap() {
      const urlCode = getAccessCodeFromUrl();
      const stored = getStoredAccessSession();
      const candidate = urlCode || stored?.accessCode || "";

      if (!candidate) {
        if (alive) setChecking(false);
        return;
      }

      try {
        const confirmed = await validateAccessCode(candidate);
        if (!alive) return;
        setAccess(confirmed);
        setCode(confirmed.accessCode);
        onAccessReady?.(confirmed);
      } catch (err) {
        if (!alive) return;
        setError(getAccessErrorMessage(err, language));
      } finally {
        if (alive) setChecking(false);
      }
    }

    bootstrap();
    return () => {
      alive = false;
    };
  }, [language, onAccessReady]);

  const submit = async (event) => {
    event.preventDefault();
    setChecking(true);
    setError("");

    try {
      const confirmed = await validateAccessCode(code);
      setAccess(confirmed);
      onAccessReady?.(confirmed);
    } catch (err) {
      setError(getAccessErrorMessage(err, language));
    } finally {
      setChecking(false);
    }
  };

  if (access) return children;

  return (
    <div className="ego-wrapper">
      <div className="ego-box" style={{ maxWidth: 520 }}>
        <p style={{ margin: 0, color: "#f6bd73", fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase" }}>
          {text.eyebrow}
        </p>
        <h2 style={{ marginTop: 10, textAlign: "center" }}>{text.title}</h2>
        <p style={{ opacity: 0.84, lineHeight: 1.5 }}>{text.description}</p>

        <p
          style={{
            margin: "14px 0 0",
            padding: "12px 14px",
            borderRadius: 12,
            background: "rgba(246,189,115,0.12)",
            border: "1px solid rgba(246,189,115,0.45)",
            fontSize: 14,
            lineHeight: 1.5,
          }}
        >
          {text.warningNote}
        </p>

        <form onSubmit={submit} style={{ marginTop: 24 }}>
          <label style={{ display: "block", marginBottom: 8, fontWeight: 700 }}>{text.label}</label>
          <input
            className="ego-input"
            value={code}
            onChange={(event) => setCode(event.target.value)}
            placeholder={text.placeholder}
            autoComplete="off"
            style={{ textTransform: "uppercase" }}
          />
          {error && <p style={{ color: "#ffb4a6", marginTop: 12 }}>{error}</p>}
          <button className="ego-btn accept" type="submit" disabled={checking} style={{ width: "100%", marginTop: 22 }}>
            {checking ? text.checking : text.button}
          </button>
        </form>

        <p style={{ marginTop: 18, marginBottom: 0, fontSize: 13, opacity: 0.7, textAlign: "center" }}>
          {text.durationNote}
        </p>
      </div>
    </div>
  );
}
